import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash'
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark'

import { toastEventDispatch } from '@space-lib-events/toast'
import { ToastPosition, ToastType } from '@space-lib-events/toast/enum'
import Button from '@space-lib/components/button'
import Icon from '@space-lib/components/icon'
import Modal from '@space-lib/components/modal'
import ModalBody from '@space-lib/components/modalBody'
import ModalFooter from '@space-lib/components/modalFooter'
import ModalHeader from '@space-lib/components/modalHeader'

import '@hotmart/cosmos/dist/styles/utilities/screenreaders.css'

import { Actions, Entities, EventNames, RequestResults, ScreenNames } from '@space-lib/datahub'

import useTranslation from '@space-lib/hooks/useTranslation'
import useMembership from '@space-shell/common/hooks/useMembership'
import { TOAST_EVENT_KEY } from '@space-shell/core/store'
import { lazyRemoteModule } from '@space-shell/lib/runtime'

import useSendDataHubConsume from '@space-shell/modules/communities/hooks/useSendDataHubConsume/useSendDataHubConsume'

import { useLocation, useNavigate } from 'react-router-dom'

import type { DeleteCommunityModalOldProps } from './types'

import { PF_SURVEY_WHY_REMOVING_COMMUNITY } from '../constants'
import { useDeleteCommunity } from '../deleteCommunityModal/hooks/useDeleteCommunity'

const DeleteCommunityModalOld = ({
  isOpen,
  closeModal,
  communityId
}: DeleteCommunityModalOldProps) => {
  const {
    t,
    i18n: { language }
  } = useTranslation()
  const { membershipSlug } = useMembership()
  const location = useLocation()
  const navigate = useNavigate()
  const { sendDataHubConsume } = useSendDataHubConsume()

  const { deleteCommunityQuery, invalidateQueries } = useDeleteCommunity()

  const handleDeleteCommunity = async () => {
    const remoteModule = await lazyRemoteModule<
      () => { productFruitsApi: { surveys: { startSurvey: (arg: string) => void } } }
    >('community/ProductFruitsContext')

    const productFruitsApi = remoteModule?.().productFruitsApi

    if (productFruitsApi) {
      productFruitsApi.surveys?.startSurvey(PF_SURVEY_WHY_REMOVING_COMMUNITY)
    }

    deleteCommunityQuery.mutate(communityId, {
      onSuccess() {
        const toastDetail = {
          type: ToastType.SUCCESS,
          message: t('modal.delete_community.success'),
          position: ToastPosition.BOTTOM_RIGHT
        }

        sendDataHubConsume({
          entity: Entities.COMMUNITY,
          action: Actions.DELETE,
          event: {
            eventName: EventNames.DELETE_COMMUNITY,
            screenName: ScreenNames.COMMUNITY_VIEW,
            communityId
          }
        })

        invalidateQueries()
        closeModal()

        if (location.pathname.endsWith(`/club/${membershipSlug}`)) {
          toastEventDispatch(toastDetail)
        } else {
          window.localStorage.setItem(TOAST_EVENT_KEY, JSON.stringify(toastDetail))

          navigate(`/${language}/club/${membershipSlug}`)
        }
      },
      onError(error) {
        sendDataHubConsume({
          entity: Entities.COMMUNITY,
          action: Actions.DELETE,
          event: {
            eventName: EventNames.DELETE_COMMUNITY,
            screenName: ScreenNames.COMMUNITY_VIEW,
            result: RequestResults.FAILURE,
            failureKind: error?.message,
            communityId
          }
        })
      }
    })
  }

  return (
    <Modal show={isOpen} onClose={closeModal}>
      <ModalHeader className='_px-5 _pt-5 _pb-4'>
        <div className='_d-flex _align-items-baseline'>
          <p className='_text-4 _m-0 _mr-4'>{t('modal.delete_community.title')}</p>
          <Button
            variation='tertiary'
            className='_p-0 _d-flex _border-0 _bg-transparent _text-gray-400 _ml-auto'
            onClick={closeModal}>
            <span className='_sr-only'>close</span>
            <Icon icon={faXmark} className='_w-5 _h-5' />
          </Button>
        </div>
      </ModalHeader>
      <ModalBody className='_p-0 _px-5'>
        <p className='_text-2 _text-gray-500 _m-0'>{t('modal.delete_community.description')}</p>
      </ModalBody>
      <ModalFooter className='_px-5 _pt-8 _pb-5'>
        <div className='_d-flex _ml-auto'>
          <Button className='_mr-5' variation='default' onClick={closeModal}>
            {t('buttons.cancel')}
          </Button>
          <Button variation='danger' onClick={handleDeleteCommunity}>
            <Icon icon={faTrash} className='_mr-2' />
            {t('buttons.delete_community')}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default DeleteCommunityModalOld
